import React, { useState, useEffect, useMemo } from 'react';
import Api from '../../api/global.service';
import { useParams, useHistory } from 'react-router-dom';
import CardContainer from '../../components/card/card-container.jsx';
import BlockUi from 'react-block-ui';
import GoogleMapReact from 'google-map-react';
import env from './../../environment.js';
import FormSelectMaterial from '../../nuup/componentes/Forms/FormSelectMaterial.jsx';
import generalCatchErrorsModal from '../../components/modal/modal-error';
import Swal from 'sweetalert2';
import CardTitleParcela from '../../components/card/card-title-parcela.jsx';
import useCroquisPuntosHook from '../../hooks/useCroquisPuntosHook.jsx';
import useGetColindancias from '../../hooks/useGetColindancias.jsx';
import useGeoPuntosLayer, {
  getGeoPuntoPositions,
  getGeometryPositions,
  getPuntoConfig,
  getPolygonFill,
  LINE_STYLES,
  OTRO_PUNTO_DEFAULT_COLOR,
  isCustomPuntoColor,
} from '../../hooks/useGeoPuntosLayer.jsx';
import NorthIcon from '../../components/map/north-icon.jsx';
import MarkerInfoWindow from '../../components/map/marker-infowindow.jsx';
import MapRecenterButton from '../../components/map/map-recenter-button.jsx';
import SimbologiaColorPicker, {
  buildUsedColors,
  getColoresRecientes,
  addColorReciente,
  normalizeHex,
} from '../../components/map/simbologia-color-picker.jsx';
import { fitMapToPositions } from './padron-mapas-list.js';
import { LineSwatch, AreaSwatch, TextSwatch } from '../../components/map/simbologia-legend.jsx';

const ColindanciaBox = ({ title, data, style }) => {
  if (!data?.colindante && !data?.propietario) return null;
  return (
    <div
      style={{
        position: 'absolute',
        zIndex: 10,
        backgroundColor: 'rgba(255,255,255,0.92)',
        border: '1px solid #c8d1a0',
        borderRadius: 6,
        padding: '5px 10px',
        maxWidth: 160,
        textAlign: 'center',
        boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
        pointerEvents: 'none',
        ...style,
      }}>
      <div style={{ fontWeight: 700, fontSize: 11, color: '#4A6700', textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</div>
      {data?.colindante && <div style={{ fontSize: 11, color: '#333', marginTop: 1 }}>{data.colindante}</div>}
      {data?.colindante_otro && <div style={{ fontSize: 11, color: '#555' }}>{data.colindante_otro}</div>}
      {data?.propietario && <div style={{ fontSize: 10, color: '#666', marginTop: 1 }}>{data.propietario}</div>}
    </div>
  );
};

// GoogleMapReact posiciona a sus hijos directos con las props lat/lng.
const CentroidMarker = () => (
  <div style={{ pointerEvents: 'none' }}>
    <MarkerInfoWindow productor={false} color={'#91B833'} />
  </div>
);

// Order: Punto → Texto → Línea → Polígono
const TIPO_OPTIONS = [
  { value: 'punto', label: 'Punto', icon: 'fa-map-marker-alt' },
  { value: 'texto', label: 'Texto', icon: 'fa-font' },
  { value: 'linea', label: 'Línea', icon: 'fa-chart-line' },
  { value: 'poligono', label: 'Polígono', icon: 'fa-draw-polygon' },
];

const CATALOGO_TIPO_POR_TIPO = { punto: 'punto', linea: 'linea', poligono: 'area' };
const NOMBRE_OTRO = 'Otro';

const FORMA_LINEA_OPTIONS = [
  { value: 'solid', label: 'Sólida' },
  { value: 'dashed', label: 'Discontinua' },
  { value: 'dotted', label: 'Punteada' },
  { value: 'dashdot', label: 'Guión-punto' },
];

const tipoButtonStyle = (isActive, disabled = false) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  padding: '6px 16px',
  borderRadius: 8,
  border: `1px solid ${isActive ? env.colors.primary_300 : env.colors.neutral_100}`,
  background: isActive ? env.colors.primary_300 : disabled ? '#f5f5f5' : 'white',
  color: isActive ? 'white' : disabled ? env.colors.neutral_300 : env.colors.neutral_700,
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: 13,
  fontWeight: isActive ? 600 : 400,
});

const ParcelaEditSimbologia = () => {
  const { uuid, geoId } = useParams();
  const history = useHistory();
  const isEditMode = !!geoId;

  const [blocking, setBlocking] = useState(false);
  const [blockingCol, setBlockingCol] = useState(false);
  const [mapType, setMapType] = useState('hybrid');
  const [parcela, setParcela] = useState({});
  const [parcelaData, setParcelaData] = useState({});
  const [allGeoPuntos, setAllGeoPuntos] = useState([]);
  const [hiddenGeoIds, setHiddenGeoIds] = useState(new Set());
  const [tipo, setTipo] = useState('punto');
  const [selectedNombre, setSelectedNombre] = useState(0);
  const [customNombre, setCustomNombre] = useState('');
  const [color, setColor] = useState('#4A6700');
  const [formaLinea, setFormaLinea] = useState('solid');
  const [initialGeometry, setInitialGeometry] = useState(null);
  const [ready, setReady] = useState(false);
  const [catalogoNombres, setCatalogoNombres] = useState([]);
  // Últimos colores elegidos (localStorage); se lee una vez y se refresca al guardar.
  const [coloresRecientes, setColoresRecientes] = useState(() => getColoresRecientes());

  useEffect(() => {
    Api.getAll('cat_simbologia_nombres', { nivel: 'parcela' })
      .then(response => setCatalogoNombres(response.data || []))
      .catch(() => setCatalogoNombres([]));
  }, []);

  const opcionesNombreTipo = useMemo(() => catalogoNombres.filter(c => c.tipo === CATALOGO_TIPO_POR_TIPO[tipo]), [catalogoNombres, tipo]);
  // Colores ya guardados en esta parcela que fueron elección del usuario (los puntos con el color legado no cuentan).
  const coloresUsados = useMemo(
    () =>
      buildUsedColors(allGeoPuntos, item =>
        item.tipo === 'punto' ? isCustomPuntoColor(item.color) : ['linea', 'area', 'poligono'].includes(item.tipo) && !!item.color
      ),
    [allGeoPuntos]
  );

  const catalogoIncluyeOtro = useMemo(() => opcionesNombreTipo.some(o => o.nombre === NOMBRE_OTRO), [opcionesNombreTipo]);

  const visibleForMap = useMemo(() => allGeoPuntos.filter(p => !hiddenGeoIds.has(p.id) && (!isEditMode || p.id !== parseInt(geoId))), [
    allGeoPuntos,
    hiddenGeoIds,
    geoId,
    isEditMode,
  ]);

  const esOtroPunto = tipo === 'punto' && selectedNombre === NOMBRE_OTRO;
  // Color original del nombre elegido (catálogo, o el color base de "Otro") para ofrecerlo de vuelta en el selector de color.
  const colorOriginal = useMemo(() => {
    if (esOtroPunto) return { color: OTRO_PUNTO_DEFAULT_COLOR, label: null };
    const opcion = selectedNombre ? opcionesNombreTipo.find(o => o.nombre === selectedNombre) : null;
    return opcion?.color_default ? { color: opcion.color_default, label: opcion.nombre } : { color: null, label: null };
  }, [esOtroPunto, selectedNombre, opcionesNombreTipo]);
  const nombreElegido = !!selectedNombre;

  const { renderMap, center, zoom, drawnGeometry, map, maps } = useCroquisPuntosHook(
    parcelaData,
    [],
    tipo,
    ready ? initialGeometry : null,
    16,
    color,
    formaLinea,
    customNombre,
    esOtroPunto ? color : null
  );

  useGeoPuntosLayer(map, maps, visibleForMap);
  const { colindancias } = useGetColindancias({ uuid, setBlocking: setBlockingCol });

  useEffect(() => {
    setTipo();
    setSelectedNombre(0);
    setCustomNombre('');
    setColor('#4A6700');
    setFormaLinea('solid');
    setInitialGeometry(null);
    setReady(false);
    setParcelaData({});
    setAllGeoPuntos([]);

    if (uuid != null) {
      setBlocking(true);

      const profilePromise = Api.get('parcela_profile', uuid)
        .then(response => {
          setParcela(response.data);
          let cent;
          if (response?.data?.centroide_pol_lat && !isNaN(response?.data?.centroide_pol_lat)) {
            cent = { lat: parseFloat(response.data.centroide_pol_lat), lng: parseFloat(response.data.centroide_pol_lng) };
          } else if (!isNaN(response?.data?.centroide_lat) && !isNaN(response?.data?.centroide_lng)) {
            cent = { lat: parseFloat(response.data.centroide_lat), lng: parseFloat(response.data.centroide_lng) };
          }
          setParcelaData({ parcelaPolygon: response?.data?.poligono, centroid: cent });
        })
        .catch(e => {
          generalCatchErrorsModal(e.response?.status, '/admin/parcelas/list');
        });

      const puntosPromise = Api.get('parcela_geos_puntos', uuid)
        .then(response => {
          const allPuntos = response.data || [];
          setAllGeoPuntos(allPuntos);

          if (isEditMode) {
            const targetId = parseInt(geoId);
            const target = allPuntos.find(p => p.id === targetId);
            if (target) {
              setTipo(target.tipo);
              const catalogoTipoDestino = CATALOGO_TIPO_POR_TIPO[target.tipo];
              const esNombreCatalogo =
                catalogoNombres.some(c => c.tipo === catalogoTipoDestino && c.nombre === target.nombre) && target.nombre !== NOMBRE_OTRO;
              if (esNombreCatalogo) {
                setSelectedNombre(target.nombre);
              } else {
                setSelectedNombre(NOMBRE_OTRO);
                setCustomNombre(target.nombre || '');
              }
              if (target.tipo === 'linea') {
                setColor(target.color || '#4A6700');
                setFormaLinea(target.forma_linea || 'solid');
              }
              if (target.tipo === 'punto') {
                setColor(isCustomPuntoColor(target.color) ? target.color : OTRO_PUNTO_DEFAULT_COLOR);
              }
              if (target.tipo === 'punto' || target.tipo === 'texto') {
                setInitialGeometry({ lat: parseFloat(target.lat), lng: parseFloat(target.lng) });
              } else if (target.tipo === 'linea' || target.tipo === 'poligono') {
                setInitialGeometry(target.coordinates);
              }
            }
          }
        })
        .catch(() => setAllGeoPuntos([]));

      Promise.all([profilePromise, puntosPromise]).finally(() => {
        setBlocking(false);
        setReady(true);
      });
    }
  }, [uuid, geoId]);

  const navigateTo = path => {
    if (drawnGeometry) {
      Swal.fire({
        text: 'Si cambias a otro símbolo se perderán tus cambios ¿quieres continuar sin guardar?',
        showDenyButton: true,
        confirmButtonText: 'Seguir editando',
        denyButtonText: 'Continuar sin guardar',
      }).then(result => {
        if (result.isDenied) history.push(path);
      });
    } else {
      history.push(path);
    }
  };

  const handleBack = () => {
    if (drawnGeometry) {
      Swal.fire({
        text: 'Los cambios actuales se podrían perder. ¿Quieres salir sin guardar?',
        showDenyButton: true,
        confirmButtonText: 'Seguir editando',
        denyButtonText: 'Salir sin guardar',
      }).then(result => {
        if (result.isDenied) history.replace(`/admin/parcelas/perfil/${uuid}`);
      });
    } else {
      history.replace(`/admin/parcelas/perfil/${uuid}`);
    }
  };

  const handleTipoChange = value => {
    setTipo(value);
    if (value === 'texto') {
      setSelectedNombre(NOMBRE_OTRO);
      setCustomNombre('');
    } else {
      setSelectedNombre(0);
      setCustomNombre('');
    }
    if (value !== 'linea') {
      setColor('#4A6700');
      setFormaLinea('solid');
    }
  };

  const handleNombreSelect = nombre => {
    setSelectedNombre(nombre);
    if (nombre !== NOMBRE_OTRO) setCustomNombre('');
    if (nombre === NOMBRE_OTRO && tipo === 'punto' && !isEditMode) setColor(OTRO_PUNTO_DEFAULT_COLOR);
    if (tipo === 'linea' && nombre !== NOMBRE_OTRO && !isEditMode) {
      const opcion = opcionesNombreTipo.find(o => o.nombre === nombre);
      if (opcion?.color_default) setColor(opcion.color_default);
    }
  };

  const getNombreFinal = () => {
    if (!selectedNombre || selectedNombre === 0) return '';
    if (selectedNombre === NOMBRE_OTRO) return customNombre.trim();
    return selectedNombre;
  };

  const isSubmitDisabled = () => !drawnGeometry || !getNombreFinal();

  const onSubmit = () => {
    const nombre = getNombreFinal();
    if (!drawnGeometry || !nombre) return;
    setBlocking(true);
    const enviaColor = tipo === 'linea' || esOtroPunto;
    // Se captura antes de la llamada: el reset posterior al guardado no debe afectarlo.
    const colorOriginalGuardado = colorOriginal.color;
    const payload = {
      uuid,
      tipo,
      nombre,
      geometria: drawnGeometry,
      ...(tipo === 'linea' ? { color, forma_linea: formaLinea } : esOtroPunto ? { color } : {}),
    };
    const apiCall = isEditMode ? Api.update('parcela_geos_puntos', geoId, payload) : Api.create('parcela_geos_puntos', payload);

    apiCall
      .then(() => {
        setBlocking(false);
        // Recientes solo guarda colores que el usuario realmente eligió (distintos del original del catálogo).
        if (enviaColor && normalizeHex(color) !== normalizeHex(colorOriginalGuardado)) setColoresRecientes(addColorReciente(color));
        Swal.fire({
          icon: 'success',
          title: '',
          text: isEditMode ? 'Cambios guardados correctamente' : 'Guardado correctamente',
          confirmButtonText: 'OK',
        }).then(result => {
          if (result.isConfirmed) {
            if (isEditMode) {
              history.push(`/admin/parcelas/perfil/editsimbologia/${uuid}`);
            } else {
              window.location.reload();
            }
          }
        });
      })
      .catch(error => {
        setBlocking(false);
        Swal.fire({ icon: 'error', title: '', text: error.response?.data?.message || 'Ocurrió un error al guardar' });
      });
  };

  const handleDelete = item => {
    const label = item.tipo === 'punto' ? 'punto' : item.tipo === 'texto' ? 'texto' : item.tipo === 'linea' ? 'línea' : 'área';
    Swal.fire({
      title: `¿Eliminar ${label}?`,
      text: `Se eliminará "${item.nombre || label}" permanentemente.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#c0392b',
    }).then(result => {
      if (result.isConfirmed) {
        Api.delete('parcela_geos_puntos', item.id)
          .then(() => window.location.reload())
          .catch(() => Swal.fire({ icon: 'error', title: 'Error al eliminar' }));
      }
    });
  };

  const toggleHidden = id => {
    setHiddenGeoIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const panTo = pos => {
    if (map) map.panTo(pos);
  };

  const recenterMap = () => {
    const positions = [];
    if (parcelaData.parcelaPolygon) {
      try {
        JSON.parse(parcelaData.parcelaPolygon).coordinates[0].forEach(([lng, lat]) => positions.push({ lat, lng }));
      } catch (e) {
        // sin polígono legible, se usa el centroide
      }
    }
    if (!positions.length && parcelaData.centroid) positions.push(parcelaData.centroid);
    positions.push(...getGeoPuntoPositions(visibleForMap), ...getGeometryPositions(drawnGeometry));
    fitMapToPositions(map, maps, positions, { padding: 100, maxZoom: 19 });
  };

  const coordsCenter = coords => ({
    lat: coords.reduce((s, c) => s + c.lat, 0) / coords.length,
    lng: coords.reduce((s, c) => s + c.lng, 0) / coords.length,
  });

  const pointsForDisplay = useMemo(
    () => allGeoPuntos.filter(p => p.tipo === 'punto').map((p, i) => ({ ...p, config: getPuntoConfig(p.nombre, i, p.color) })),
    [allGeoPuntos]
  );
  const linesForDisplay = useMemo(
    () =>
      allGeoPuntos
        .filter(p => p.tipo === 'linea')
        .map((p, i) => ({
          ...p,
          lineStyle: p.color ? { color: p.color, weight: 3, dashType: p.forma_linea || 'solid' } : LINE_STYLES[i % LINE_STYLES.length],
        })),
    [allGeoPuntos]
  );
  const polygonsForDisplay = useMemo(
    () => allGeoPuntos.filter(p => p.tipo === 'poligono').map((p, i) => ({ ...p, fill: getPolygonFill(i) })),
    [allGeoPuntos]
  );
  const textsForDisplay = useMemo(() => allGeoPuntos.filter(p => p.tipo === 'texto'), [allGeoPuntos]);

  const hasLegend = allGeoPuntos.length > 0;
  const activeGeoId = isEditMode ? parseInt(geoId) : null;

  const hasSectionAfterPointsTexts = linesForDisplay.length > 0 || polygonsForDisplay.length > 0;
  const hasSectionAfterLines = polygonsForDisplay.length > 0;

  const SimboloActions = ({ item }) => (
    <div style={{ display: 'flex', gap: 10, flexShrink: 0, marginLeft: 'auto' }}>
      <i
        className="fa fa-pencil-alt"
        title="Editar"
        style={{
          color: item.id === activeGeoId ? '#bbb' : '#507736',
          cursor: item.id === activeGeoId ? 'default' : 'pointer',
          fontSize: 13,
        }}
        onClick={e => {
          e.stopPropagation();
          if (item.id === activeGeoId) return;
          navigateTo(`/admin/parcelas/perfil/editsimbologia/${uuid}/${item.id}`);
        }}
      />
      <i
        className="fa fa-trash"
        title="Eliminar"
        style={{ color: '#c0392b', cursor: 'pointer', fontSize: 13 }}
        onClick={e => {
          e.stopPropagation();
          handleDelete(item);
        }}
      />
      <i
        className={hiddenGeoIds.has(item.id) ? 'fa fa-eye-slash' : 'fa fa-eye'}
        title={hiddenGeoIds.has(item.id) ? 'Mostrar en mapa' : 'Ocultar en mapa'}
        style={{ color: '#777', cursor: 'pointer', fontSize: 13 }}
        onClick={e => {
          e.stopPropagation();
          toggleHidden(item.id);
        }}
      />
    </div>
  );

  const SimboloItem = ({ children, onClick, isHidden }) => (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: 6,
        cursor: 'pointer',
        opacity: isHidden ? 0.4 : 1,
      }}>
      {children}
    </div>
  );

  const activeTipoLabel = TIPO_OPTIONS.find(o => o.value === tipo)?.label || tipo;

  return (
    <>
      {/* Header row */}
      <div className="row justify-content-between mb-3 align-items-center">
        <button onClick={handleBack} type="button" className="btn btn-outline-secondary btn-sirio" style={{ borderRadius: 20 }}>
          <i className="fa fa-arrow-left mr-2" />
          Volver
        </button>
        <FormSelectMaterial
          id="tipoMapa"
          error={false}
          label="Tipo Mapa"
          value={mapType}
          onChange={v => setMapType(v)}
          height={36}
          options={[
            { value: 'satellite', label: 'Satelital' },
            { value: 'terrain', label: 'Terreno' },
            { value: 'hybrid', label: 'Híbrido' },
          ]}
          showAll={false}
        />
      </div>

      <BlockUi tag="div" message="" blocking={blocking || blockingCol} renderChildren={true}>
        <CardContainer style={{ paddingTop: 16 }}>
          {/* Parcela info */}
          <div className="row mb-3">
            <div className="col-4">
              <div className="row">
                <CardTitleParcela
                  cuenta_uuid={parcela.cuenta_uuid}
                  cuenta_name={parcela.cuenta}
                  parcela_name={parcela.nombre}
                  localidad={parcela.localidad}
                  municipio={parcela.municipio}
                />
              </div>
            </div>
            <div className="col-8">
              <div className="row mb-3">
                {/* Spacer — pushes Guardar to the far right */}
                <div style={{ flex: 1, minWidth: 16 }} />

                <button
                  onClick={onSubmit}
                  type="button"
                  disabled={isSubmitDisabled()}
                  className="btn btn-outline btn-sirio"
                  style={{ borderRadius: 20, whiteSpace: 'nowrap', paddingLeft: 28, paddingRight: 28, flexShrink: 0 }}>
                  Guardar
                </button>
                {isEditMode && (
                  <button
                    onClick={() => navigateTo(`/admin/parcelas/perfil/editsimbologia/${uuid}`)}
                    type="button"
                    className="btn btn-outline-secondary btn-sirio"
                    style={{ borderRadius: 20, whiteSpace: 'nowrap', paddingLeft: 20, paddingRight: 20, flexShrink: 0, marginLeft: 10 }}>
                    Cancelar
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Inputs card — single row, wraps on small screens */}
          <div
            style={{
              background: env.colors.neutral_2,
              border: `1px solid ${env.colors.neutral_100}`,
              borderRadius: 8,
              padding: '10px 18px',
              marginBottom: 14,
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              rowGap: 10,
            }}>
            {/* Group 1: mode label + tipo buttons */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  textTransform: 'uppercase',
                  color: env.colors.neutral_700,
                  marginRight: 4,
                }}>
                {isEditMode ? 'Editando' : 'Nuevo'}
              </span>
              {isEditMode ? (
                <span style={tipoButtonStyle(true, false)}>
                  <i className={`fa ${TIPO_OPTIONS.find(o => o.value === tipo)?.icon || 'fa-map-marker-alt'}`} />
                  {activeTipoLabel}
                </span>
              ) : (
                <>
                  {TIPO_OPTIONS.map(({ value, label, icon }) => (
                    <button key={value} type="button" onClick={() => handleTipoChange(value)} style={tipoButtonStyle(tipo === value)}>
                      <i className={`fa ${icon}`} />
                      {label}
                    </button>
                  ))}
                </>
              )}
            </div>

            {/* Vertical separator */}
            <div
              style={{
                width: 1,
                alignSelf: 'stretch',
                minHeight: 32,
                backgroundColor: env.colors.neutral_100,
                margin: '0 16px',
                flexShrink: 0,
              }}
            />

            {/* Group 2: nombre dropdown + optional custom input */}
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
              {tipo !== 'texto' && (
                <div style={{ minWidth: 200 }}>
                  <FormSelectMaterial
                    id="nombre"
                    error={false}
                    label="Nombre"
                    value={selectedNombre}
                    onChange={handleNombreSelect}
                    height={36}
                    options={[
                      ...opcionesNombreTipo.map(o => ({ value: o.nombre, label: o.nombre })),
                      ...(catalogoIncluyeOtro ? [] : [{ value: NOMBRE_OTRO, label: NOMBRE_OTRO }]),
                    ]}
                    showAll={true}
                    showAllLabel="Selecciona un nombre"
                  />
                </div>
              )}
              {(selectedNombre === NOMBRE_OTRO || tipo === 'texto') && (
                <input
                  type="text"
                  className="form-control"
                  placeholder={tipo === 'texto' ? 'Texto en el mapa' : 'Escribe el nombre'}
                  value={customNombre}
                  onChange={e => setCustomNombre(e.target.value)}
                  style={{ height: 36, fontSize: 13, minWidth: 160 }}
                />
              )}
              {((tipo === 'linea' && nombreElegido) || esOtroPunto) && (
                <SimbologiaColorPicker
                  value={color}
                  onChange={setColor}
                  usedColors={coloresUsados}
                  recentColors={coloresRecientes}
                  defaultColor={colorOriginal.color}
                  defaultLabel={colorOriginal.label}
                />
              )}
              {tipo === 'linea' && nombreElegido && (
                <div style={{ minWidth: 160 }}>
                  <FormSelectMaterial
                    id="formaLinea"
                    error={false}
                    label="Tipo"
                    value={formaLinea}
                    onChange={setFormaLinea}
                    height={36}
                    options={FORMA_LINEA_OPTIONS}
                    showAll={false}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Full-width map with colindancias */}
          <div style={{ position: 'relative', width: '100%', height: 480, marginBottom: 14 }}>
            <ColindanciaBox title="Norte" data={colindancias?.norte} style={{ top: 8, left: '50%', transform: 'translateX(-50%)' }} />
            <ColindanciaBox title="Sur" data={colindancias?.sur} style={{ bottom: 8, left: '50%', transform: 'translateX(-50%)' }} />
            <ColindanciaBox title="Oeste" data={colindancias?.oeste} style={{ top: '50%', left: 8, transform: 'translateY(-50%)' }} />
            <ColindanciaBox title="Este" data={colindancias?.este} style={{ top: '50%', right: 8, transform: 'translateY(-50%)' }} />
            <div className="map-rounded" style={{ height: '100%', width: '100%' }}>
              <NorthIcon color={mapType === 'terrain' ? 'black' : 'white'} />
              {(parcelaData.parcelaPolygon || parcelaData.centroid) && (!isEditMode || ready) && (
                <MapRecenterButton onClick={recenterMap} />
              )}
              {(parcelaData.parcelaPolygon || parcelaData.centroid) && (!isEditMode || ready) ? (
                <GoogleMapReact
                  bootstrapURLKeys={{ key: env.googleApiKey, libraries: ['geometry'] }}
                  options={() => ({ mapTypeId: mapType })}
                  center={center}
                  zoom={zoom}
                  yesIWantToUseGoogleMapApiInternals
                  onGoogleApiLoaded={({ map, maps }) => renderMap(map, maps)}>
                  {/* Sin polígono: se muestra el centroide como referencia. pointerEvents: 'none'
                      para no bloquear los clics de dibujo sobre el mapa. */}
                  {!parcelaData.parcelaPolygon && parcelaData.centroid && (
                    <CentroidMarker lat={parcelaData.centroid.lat} lng={parcelaData.centroid.lng} />
                  )}
                </GoogleMapReact>
              ) : null}
            </div>
          </div>

          {/* Simbología card */}
          {(hasLegend || isEditMode) && (
            <div
              style={{
                border: '1px solid #c8d1a0',
                borderRadius: 8,
                padding: '12px 16px',
                backgroundColor: '#f9faf4',
                marginBottom: 8,
              }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#4A6700', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                  Simbología
                </div>
              </div>

              {/* Sections in a flex row with vertical dividers */}
              <div style={{ display: 'flex', gap: 0, flexWrap: 'wrap' }}>
                {(pointsForDisplay.length > 0 || textsForDisplay.length > 0) && (
                  <div
                    style={{
                      flex: 1,
                      minWidth: 180,
                      paddingRight: hasSectionAfterPointsTexts ? 20 : 0,
                      borderRight: hasSectionAfterPointsTexts ? `1px solid ${env.colors.neutral_100}` : 'none',
                      marginBottom: 8,
                    }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 11,
                        color: '#666',
                        marginBottom: 8,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}>
                      Puntos / Textos
                    </div>
                    {pointsForDisplay.map(item => (
                      <SimboloItem
                        key={item.id}
                        onClick={() => panTo({ lat: parseFloat(item.lat), lng: parseFloat(item.lng) })}
                        isHidden={hiddenGeoIds.has(item.id)}>
                        <img
                          src={item.config.iconUrl}
                          alt={item.config.label}
                          style={{ width: 20, height: 20, marginRight: 6, flexShrink: 0 }}
                        />
                        <span style={{ fontSize: 12, color: '#333' }}>
                          {item.config.label}
                          {item.id === activeGeoId && <em style={{ color: '#789D16', fontSize: 10 }}> (editando)</em>}
                        </span>
                        <SimboloActions item={item} />
                      </SimboloItem>
                    ))}
                    {textsForDisplay.map(item => (
                      <SimboloItem
                        key={item.id}
                        onClick={() => panTo({ lat: parseFloat(item.lat), lng: parseFloat(item.lng) })}
                        isHidden={hiddenGeoIds.has(item.id)}>
                        <TextSwatch />
                        <span style={{ fontSize: 12, color: '#333' }}>
                          {item.nombre || 'Texto'}
                          {item.id === activeGeoId && <em style={{ color: '#789D16', fontSize: 10 }}> (editando)</em>}
                        </span>
                        <SimboloActions item={item} />
                      </SimboloItem>
                    ))}
                  </div>
                )}

                {linesForDisplay.length > 0 && (
                  <div
                    style={{
                      flex: 1,
                      minWidth: 180,
                      paddingLeft: pointsForDisplay.length > 0 || textsForDisplay.length > 0 ? 20 : 0,
                      paddingRight: hasSectionAfterLines ? 20 : 0,
                      borderRight: hasSectionAfterLines ? `1px solid ${env.colors.neutral_100}` : 'none',
                      marginBottom: 8,
                    }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 11,
                        color: '#666',
                        marginBottom: 8,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}>
                      Líneas
                    </div>
                    {linesForDisplay.map((item, i) => (
                      <SimboloItem
                        key={item.id}
                        onClick={() => Array.isArray(item.coordinates) && panTo(coordsCenter(item.coordinates))}
                        isHidden={hiddenGeoIds.has(item.id)}>
                        <LineSwatch style={item.lineStyle} />
                        <span style={{ fontSize: 12, color: '#333' }}>
                          {item.nombre || `Línea ${i + 1}`}
                          {item.id === activeGeoId && <em style={{ color: '#789D16', fontSize: 10 }}> (editando)</em>}
                        </span>
                        <SimboloActions item={item} />
                      </SimboloItem>
                    ))}
                  </div>
                )}

                {polygonsForDisplay.length > 0 && (
                  <div
                    style={{
                      flex: 1,
                      minWidth: 180,
                      paddingLeft: pointsForDisplay.length > 0 || textsForDisplay.length > 0 || linesForDisplay.length > 0 ? 20 : 0,
                      marginBottom: 8,
                    }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 11,
                        color: '#666',
                        marginBottom: 8,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}>
                      Áreas
                    </div>
                    {polygonsForDisplay.map((item, i) => (
                      <SimboloItem
                        key={item.id}
                        onClick={() => Array.isArray(item.coordinates) && panTo(coordsCenter(item.coordinates))}
                        isHidden={hiddenGeoIds.has(item.id)}>
                        <AreaSwatch fill={item.fill} />
                        <span style={{ fontSize: 12, color: '#333' }}>
                          {item.nombre || `Área ${i + 1}`}
                          {item.id === activeGeoId && <em style={{ color: '#789D16', fontSize: 10 }}> (editando)</em>}
                        </span>
                        <SimboloActions item={item} />
                      </SimboloItem>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContainer>
      </BlockUi>
    </>
  );
};

export default ParcelaEditSimbologia;
